// The learner's playback-rate preference, and the rate the app actually applies.
//
// Two numbers live here and they are kept apart on purpose.
//
// THE PREFERENCE is what the learner last chose with the speed chip on a tutor
// pill. It is cached on the device under PLAYBACK_RATE_CACHE_KEY and mirrored
// to users/{uid}/audioPlaybackRate, following the theme's shape in
// src/composables/useTheme.js with two differences: the cache is cleared on
// sign-out (forgetPlaybackRate, called from authStore.clearPlaybackRate), and the
// mirror write waits until the chip has rested on one value for
// PLAYBACK_RATE_MIRROR_DELAY_MS and is skipped when the account already holds
// that value. The wait exists because FreeChatView subscribes to the whole
// users/{uid} node and re-runs its chat-resolution reads on every snapshot, so
// each tap of the chip must not become a write.
//
// THE EFFECTIVE RATE is what the audio, the iOS media-time clock, both time
// labels on the pill, the progressive reveal and the segment watchdog use. It
// is 1 whenever the chip would be hidden: while the audioPlaybackSpeed flag is
// off for this learner, and on iOS until the native plugin has reported both
// rate and range support. Otherwise it is the preference. Nothing reads the
// preference directly for playback, because two real states would then slow
// something below 1x with no chip on screen to reset it: an iOS build with this
// JavaScript and the old plugin (the reveal would slow while the audio played
// at 1x), and a learner whose flag was turned off after they chose 0.5x.
//
// The module imports nothing but Vue, so the playback service and the
// components can read it without pulling in Firebase or the auth store. The two
// inputs it does not own are fed from outside: `speedEnabled` by
// src/scripts/playbackRateSync.js (which watches the flag), and
// `platformSupported` by the playback service (which asks the plugin).

import { reactive, computed } from "vue";

// The chip's cycle, fastest first. database.rules.json carries a copy of this
// list in the users/$uid/audioPlaybackRate .validate clause, and
// tests/unit/playbackRateRulesSync.test.js pins the two together, so changing a
// step is a rules change as well.
export const PLAYBACK_RATE_STEPS = Object.freeze([1, 0.75, 0.5]);

export const PLAYBACK_RATE_CACHE_KEY = "al-playback-rate";

export const PLAYBACK_RATE_MIRROR_DELAY_MS = 2000;

/** The value itself when it is one of the steps, and 1 for anything else. */
export function normalizePlaybackRate(value) {
  return typeof value === "number" && PLAYBACK_RATE_STEPS.includes(value) ? value : 1;
}

/** The step the chip moves to from `current`, wrapping from the slowest to 1x. */
export function nextPlaybackRate(current) {
  const index = PLAYBACK_RATE_STEPS.indexOf(normalizePlaybackRate(current));
  return PLAYBACK_RATE_STEPS[(index + 1) % PLAYBACK_RATE_STEPS.length];
}

export const playbackRateState = reactive({
  // The learner's choice, always one of the steps.
  preferred: 1,
  // Whether audioPlaybackSpeed is on for the signed-in learner.
  speedEnabled: false,
  // Whether this platform can play at another rate: true on web and Android
  // as soon as the playback service loads, and on iOS only once the plugin's
  // getCapabilities has reported both rate and range support.
  platformSupported: false,
  // Bumped by every chip change and every sign-out. The login read of
  // audioPlaybackRate records it when the read starts and discards its answer
  // if it moved, so a read that was already in flight cannot undo a choice the
  // learner made while it was.
  version: 0,
});

/** Whether the chip is shown on an eligible pill at all. */
export const playbackRateAvailable = computed(
  () => playbackRateState.speedEnabled && playbackRateState.platformSupported
);

/** The rate applied to tutor audio; 1 whenever the chip would be hidden. */
export const effectivePlaybackRate = computed(() =>
  playbackRateAvailable.value ? normalizePlaybackRate(playbackRateState.preferred) : 1
);

// ---------- The device cache ----------

function readCache() {
  try {
    const raw = localStorage.getItem(PLAYBACK_RATE_CACHE_KEY);
    return raw === null ? 1 : normalizePlaybackRate(Number(raw));
  } catch {
    return 1;
  }
}

function writeCache(value) {
  try {
    localStorage.setItem(PLAYBACK_RATE_CACHE_KEY, String(value));
  } catch {
    // Storage unavailable (private mode): the choice still holds for this
    // session, and the mirror still reaches the account.
  }
}

function removeCache() {
  try {
    localStorage.removeItem(PLAYBACK_RATE_CACHE_KEY);
  } catch {
    // Nothing was stored, so there is nothing to forget.
  }
}

/** Restores the cached preference. Called once at startup. */
export function loadCachedPlaybackRate() {
  playbackRateState.preferred = readCache();
}

// ---------- The mirror ----------

// Installed by playbackRateSync.js: { currentUid() -> string|null,
// write(uid, value) -> Promise }. Kept behind this seam so the rules about when
// to write live here, beside the state they depend on, without this module
// importing Firebase.
let mirror = null;
let pendingMirrorTimer = null;
// The account a waiting write is for, and the function the timer will run, so
// a background flush can run it early.
let pendingMirrorUid = null;
let pendingMirrorFire = null;
// What the account will hold once every write already sent has landed: the
// value the login read adopted, or the value of the most recent write sent,
// for one uid. It is recorded when a write is SENT, not when it is
// acknowledged, because RTDB applies one client's writes to a path in the order
// they were sent: comparing against the last acknowledged value instead would
// skip a write that returns to it while a different write is still in flight,
// and the account would end on the in-flight value. `value` is undefined when
// nothing is known, which always writes.
let knownRemote = { uid: null, value: undefined };
// Counts writes sent, so a failure can tell whether it was the latest one.
let mirrorWriteSeq = 0;
// Writes sent and not yet settled, per uid. While one is in flight that
// account's value is about to change, so a login read's answer for the same
// account is not adopted over it.
const mirrorWritesInFlight = new Map();

export function registerPlaybackRateMirror(nextMirror) {
  mirror = nextMirror;
}

function cancelPendingMirror() {
  if (pendingMirrorTimer === null) return;
  clearTimeout(pendingMirrorTimer);
  pendingMirrorTimer = null;
  pendingMirrorUid = null;
  pendingMirrorFire = null;
}

function scheduleMirror() {
  cancelPendingMirror();
  const uid = mirror ? mirror.currentUid() : null;
  // Signed out, the choice stays on the device only.
  if (!uid) return;
  const versionAtSchedule = playbackRateState.version;
  const fire = () => {
    pendingMirrorTimer = null;
    pendingMirrorUid = null;
    pendingMirrorFire = null;
    // A sign-out bumps the version and a different account changes the uid;
    // either way this choice does not belong to whoever is signed in now.
    if (!mirror || mirror.currentUid() !== uid) return;
    if (playbackRateState.version !== versionAtSchedule) return;
    const value = playbackRateState.preferred;
    if (knownRemote.uid === uid && knownRemote.value === value) return;
    const seq = ++mirrorWriteSeq;
    knownRemote = { uid, value };
    mirrorWritesInFlight.set(uid, (mirrorWritesInFlight.get(uid) || 0) + 1);
    const activeMirror = mirror;
    Promise.resolve()
      .then(() => activeMirror.write(uid, value))
      .catch((error) => {
        // Best effort, like the theme: the device cache already holds the
        // choice. Only the latest write's failure forgets what the account
        // holds, so the next change writes again.
        if (seq === mirrorWriteSeq && knownRemote.uid === uid) {
          knownRemote = { uid, value: undefined };
        }
        console.error("Failed to sync the playback rate preference:", error);
      })
      .finally(() => {
        const remaining = (mirrorWritesInFlight.get(uid) || 1) - 1;
        if (remaining > 0) mirrorWritesInFlight.set(uid, remaining);
        else mirrorWritesInFlight.delete(uid);
      });
  };
  pendingMirrorUid = uid;
  pendingMirrorFire = fire;
  pendingMirrorTimer = setTimeout(fire, PLAYBACK_RATE_MIRROR_DELAY_MS);
}

/**
 * Sends a choice still waiting for the chip to rest, now. Called when the page
 * is hidden (the app going to the background, a tab switch, the page closing),
 * because the two-second timer does not survive the app being closed, and the
 * next launch would then adopt the account's older value over the choice.
 */
export function flushPlaybackRateMirror() {
  if (pendingMirrorTimer === null || !pendingMirrorFire) return;
  const fire = pendingMirrorFire;
  clearTimeout(pendingMirrorTimer);
  fire();
}

// ---------- Writers ----------

/**
 * The chip's write: apply, cache, and mirror once the chip comes to rest.
 * @param {number} value one of PLAYBACK_RATE_STEPS
 * @returns {number} the preference now in force
 */
export function choosePlaybackRate(value) {
  const next = normalizePlaybackRate(value);
  playbackRateState.version++;
  if (next !== playbackRateState.preferred) {
    playbackRateState.preferred = next;
    writeCache(next);
  }
  scheduleMirror();
  return next;
}

/**
 * Adopt the value read from users/{uid}/audioPlaybackRate at login, without
 * writing it back. A value outside the step list is adopted as 1x.
 * @param {*} value what the read returned
 * @param {string} uid the account it was read from
 */
export function adoptStoredPlaybackRate(value, uid) {
  // A choice still waiting to be mirrored, or a write of one still in flight,
  // is newer than anything a read can return: the learner made it on this
  // device, and the account is about to hold it. The read's value is dropped.
  if ((pendingMirrorTimer !== null && pendingMirrorUid === uid) || mirrorWritesInFlight.has(uid)) {
    return;
  }
  const next = normalizePlaybackRate(value);
  if (uid) knownRemote = { uid, value: next === value ? next : undefined };
  if (next === playbackRateState.preferred) return;
  playbackRateState.preferred = next;
  writeCache(next);
}

/**
 * Called when a login read of users/{uid}/audioPlaybackRate starts. The read's
 * answer is only worth adopting if nothing on this device made it stale, which
 * playbackRateReadIsCurrent decides from this marker when the read returns.
 */
export function beginPlaybackRateRead(uid) {
  return {
    version: playbackRateState.version,
    writeSeq: mirrorWriteSeq,
    // A choice waiting to be mirrored to this account, or a write to it still
    // in flight, when the read starts means the read can return the account's
    // older value even if that write lands before the read's answer is used.
    // Scoped to the account being read: a write the previous account left in
    // flight says nothing about this one.
    localChoicePending:
      (pendingMirrorTimer !== null && pendingMirrorUid === uid) ||
      mirrorWritesInFlight.has(uid),
  };
}

/**
 * Whether a read begun with `marker` may still be adopted: no choice was
 * pending when it began, and since then the chip has not moved, no write has
 * been sent, and nobody has signed out.
 */
export function playbackRateReadIsCurrent(marker) {
  return (
    !!marker &&
    !marker.localChoicePending &&
    marker.version === playbackRateState.version &&
    marker.writeSeq === mirrorWriteSeq
  );
}

/** Sign-out: forget the preference in memory and on the device. */
export function forgetPlaybackRate() {
  cancelPendingMirror();
  playbackRateState.version++;
  playbackRateState.preferred = 1;
  knownRemote = { uid: null, value: undefined };
  removeCache();
}
